import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  PictureAsPdf,
  Refresh,
  Close,
  Login,
  Logout,
  Edit,
  Add,
  Delete,
  PlayArrow,
  NavigateNext,
  Warning,
  Error,
  Info,
  Security,
} from '@mui/icons-material';
import { AUDIT_LOGS_QUERY, AuditLogItem, AuditLogConnection, AuditLogFilters } from '../graphql/auditlog';
import { GET_COMPANY_USERS, CompanyUser } from '../graphql/settings';
import jsPDF from 'jspdf';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// Category icons and colors
const categoryConfig: Record<string, { icon: React.ReactNode; color: string; bgColor: string }> = {
  auth: { icon: <Login fontSize="small" />, color: '#1e3a5f', bgColor: '#e0f2fe' },
  sequence: { icon: <Edit fontSize="small" />, color: '#0891b2', bgColor: '#cffafe' },
  execution: { icon: <PlayArrow fontSize="small" />, color: '#16a34a', bgColor: '#dcfce7' },
  navigation: { icon: <NavigateNext fontSize="small" />, color: '#6b7280', bgColor: '#f3f4f6' },
  alert: { icon: <Warning fontSize="small" />, color: '#ea580c', bgColor: '#ffedd5' },
  settings: { icon: <Security fontSize="small" />, color: '#4f46e5', bgColor: '#e0e7ff' },
  export: { icon: <PictureAsPdf fontSize="small" />, color: '#be185d', bgColor: '#fce7f3' },
  api: { icon: <Security fontSize="small" />, color: '#0f766e', bgColor: '#ccfbf1' },
};

// Severity colors
const severityConfig: Record<string, { color: string; bgColor: string }> = {
  info: { color: '#0284c7', bgColor: '#e0f2fe' },
  warning: { color: '#ca8a04', bgColor: '#fef9c3' },
  error: { color: '#dc2626', bgColor: '#fee2e2' },
  critical: { color: '#7f1d1d', bgColor: '#fecaca' },
};

// Action icons
const actionIcons: Record<string, React.ReactNode> = {
  login: <Login fontSize="small" />,
  logout: <Logout fontSize="small" />,
  login_failed: <Error fontSize="small" />,
  create: <Add fontSize="small" />,
  update: <Edit fontSize="small" />,
  delete: <Delete fontSize="small" />,
  execute: <PlayArrow fontSize="small" />,
  view: <Visibility fontSize="small" />,
  navigate: <NavigateNext fontSize="small" />,
};

const AuditLog: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLogItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const { data, loading, error, refetch } = useQuery<{ auditLogs: AuditLogConnection }>(AUDIT_LOGS_QUERY, {
    variables: {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      filters: Object.keys(filters).length > 0 ? filters : null,
    },
    fetchPolicy: 'cache-and-network',
  });

  // Fetch users for the filter dropdown
  const { data: usersData } = useQuery<{ companyUsers: CompanyUser[] }>(GET_COMPANY_USERS);
  const companyUsers = usersData?.companyUsers || [];

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof AuditLogFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined,
    }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(0);
  };

  const handleViewDetail = (log: AuditLogItem) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  const handleExportPdf = async () => {
    if (!auditLogs.length) return;

    setExportingPdf(true);
    try {
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 14;
      let yPosition = 15;

      // Title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Audit Log Report', margin, yPosition);
      yPosition += 8;

      // Metadata
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += 5;
      pdf.text(`Total Records: ${totalCount}`, margin, yPosition);
      yPosition += 10;

      // Table headers
      const colWidths = [35, 25, 22, 85, 28, 20, 50];
      const headers = ['Timestamp', 'Category', 'Action', 'Description', 'User', 'Severity', 'Target'];
      
      const drawTableHeader = () => {
        pdf.setFillColor(241, 245, 249);
        pdf.rect(margin, yPosition - 4, pdfWidth - margin * 2, 8, 'F');
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        let xPos = margin;
        headers.forEach((header, i) => {
          pdf.text(header, xPos + 1, yPosition);
          xPos += colWidths[i];
        });
        yPosition += 6;
        pdf.setFont('helvetica', 'normal');
      };

      drawTableHeader();

      // Table rows
      pdf.setFontSize(8);
      auditLogs.forEach((log, index) => {
        // Check if we need a new page
        if (yPosition > pdfHeight - 15) {
          pdf.addPage();
          yPosition = 15;
          drawTableHeader();
        }

        // Alternate row background
        if (index % 2 === 0) {
          pdf.setFillColor(248, 250, 252);
          pdf.rect(margin, yPosition - 3, pdfWidth - margin * 2, 6, 'F');
        }

        pdf.setTextColor(51, 65, 85);
        let xPos = margin;

        // Timestamp
        const timestamp = new Date(log.createdAt).toLocaleString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        pdf.text(timestamp.substring(0, 20), xPos + 1, yPosition);
        xPos += colWidths[0];

        // Category
        pdf.text(log.categoryDisplay || log.category || '-', xPos + 1, yPosition);
        xPos += colWidths[1];

        // Action
        pdf.text(log.actionDisplay || log.action || '-', xPos + 1, yPosition);
        xPos += colWidths[2];

        // Description (truncate if needed)
        const desc = log.title || '-';
        pdf.text(desc.substring(0, 55) + (desc.length > 55 ? '...' : ''), xPos + 1, yPosition);
        xPos += colWidths[3];

        // User
        pdf.text(log.userName || 'System', xPos + 1, yPosition);
        xPos += colWidths[4];

        // Severity (with color)
        const sevColors: Record<string, [number, number, number]> = {
          info: [2, 132, 199],
          warning: [202, 138, 4],
          error: [220, 38, 38],
          critical: [127, 29, 29],
        };
        const sevColor = sevColors[log.severity] || [100, 116, 139];
        pdf.setTextColor(sevColor[0], sevColor[1], sevColor[2]);
        pdf.setFont('helvetica', 'bold');
        pdf.text((log.severity || '-').toUpperCase(), xPos + 1, yPosition);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(51, 65, 85);
        xPos += colWidths[5];

        // Target
        pdf.text((log.targetName || '-').substring(0, 30), xPos + 1, yPosition);

        yPosition += 6;
      });

      const timestamp = new Date().toISOString().split('T')[0];
      pdf.save(`Audit-Log-${timestamp}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
    } finally {
      setExportingPdf(false);
    }
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const parseChanges = (changesStr: string) => {
    try {
      return JSON.parse(changesStr);
    } catch {
      return {};
    }
  };

  const parseMetadata = (metadataStr: string) => {
    try {
      return JSON.parse(metadataStr);
    } catch {
      return {};
    }
  };

  const auditLogs = data?.auditLogs.items || [];
  const totalCount = data?.auditLogs.totalCount || 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Audit Log
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track all system activities, changes, and user actions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ borderColor: showFilters ? '#1e3a5f' : undefined, color: showFilters ? '#1e3a5f' : undefined }}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={exportingPdf ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
            onClick={handleExportPdf}
            disabled={exportingPdf || auditLogs.length === 0}
            sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152a4a' } }}
          >
            {exportingPdf ? 'Exporting...' : 'Export PDF'}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.category || ''}
                    label="Category"
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="auth">Authentication</MenuItem>
                    <MenuItem value="sequence">Sequence</MenuItem>
                    <MenuItem value="execution">Execution</MenuItem>
                    <MenuItem value="navigation">Navigation</MenuItem>
                    <MenuItem value="alert">Alert</MenuItem>
                    <MenuItem value="settings">Settings</MenuItem>
                    <MenuItem value="export">Export</MenuItem>
                    <MenuItem value="api">API Access</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={filters.action || ''}
                    label="Action"
                    onChange={(e) => handleFilterChange('action', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="login">Login</MenuItem>
                    <MenuItem value="logout">Logout</MenuItem>
                    <MenuItem value="login_failed">Login Failed</MenuItem>
                    <MenuItem value="create">Create</MenuItem>
                    <MenuItem value="update">Update</MenuItem>
                    <MenuItem value="delete">Delete</MenuItem>
                    <MenuItem value="execute">Execute</MenuItem>
                    <MenuItem value="view">View</MenuItem>
                    <MenuItem value="navigate">Navigate</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Severity</InputLabel>
                  <Select
                    value={filters.severity || ''}
                    label="Severity"
                    onChange={(e) => handleFilterChange('severity', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="info">Info</MenuItem>
                    <MenuItem value="warning">Warning</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>User</InputLabel>
                  <Select
                    value={filters.userId || ''}
                    label="User"
                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                  >
                    <MenuItem value="">All Users</MenuItem>
                    {companyUsers.map((user) => (
                      <MenuItem key={user.userId} value={String(user.userId)}>
                        {user.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From Date"
                  value={filters.dateFrom?.split('T')[0] || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value ? new Date(e.target.value).toISOString() : '')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="To Date"
                    value={filters.dateTo?.split('T')[0] || ''}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value ? new Date(e.target.value).toISOString() : '')}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClearFilters}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Stats Summary */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Chip
          label={`Total: ${totalCount.toLocaleString()}`}
          sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }}
        />
        {Object.keys(filters).filter(k => filters[k as keyof AuditLogFilters]).length > 0 && (
          <Chip
            label={`Filters Applied: ${Object.keys(filters).filter(k => filters[k as keyof AuditLogFilters]).length}`}
            color="primary"
            size="small"
          />
        )}
      </Box>

      {/* Table */}
      <Card>
        <TableContainer component={Paper}>
          {loading && !data ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="error">Error loading audit logs: {error.message}</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: 160 }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 120 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 80 }}>Severity</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 60 }} align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No audit logs found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log) => {
                    const catConfig = categoryConfig[log.category] || categoryConfig.api;
                    const sevConfig = severityConfig[log.severity] || severityConfig.info;

                    return (
                      <TableRow
                        key={log.id}
                        hover
                        sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {formatDate(log.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={catConfig.icon as React.ReactElement}
                            label={log.categoryDisplay}
                            size="small"
                            sx={{
                              bgcolor: catConfig.bgColor,
                              color: catConfig.color,
                              fontWeight: 500,
                              '& .MuiChip-icon': { color: catConfig.color },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {actionIcons[log.action] || <Info fontSize="small" />}
                            <Typography variant="body2">{log.actionDisplay}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {log.title}
                          </Typography>
                          {log.targetName && (
                            <Typography variant="caption" color="text.secondary">
                              Target: {log.targetName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {log.userName || 'System'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.severity}
                            size="small"
                            sx={{
                              bgcolor: sevConfig.bgColor,
                              color: sevConfig.color,
                              fontWeight: 500,
                              textTransform: 'capitalize',
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetail(log)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Detail Modal */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#0f172a', color: 'white' }}>
          <Typography variant="h6" sx={{ color: 'white' }}>Audit Log Details</Typography>
          <IconButton onClick={() => setDetailOpen(false)} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedLog && (
            <Box>
              {/* Header Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {selectedLog.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={selectedLog.categoryDisplay}
                    size="small"
                    sx={{
                      bgcolor: (categoryConfig[selectedLog.category] || categoryConfig.api).bgColor,
                      color: (categoryConfig[selectedLog.category] || categoryConfig.api).color,
                    }}
                  />
                  <Chip
                    label={selectedLog.actionDisplay}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={selectedLog.severity}
                    size="small"
                    sx={{
                      bgcolor: (severityConfig[selectedLog.severity] || severityConfig.info).bgColor,
                      color: (severityConfig[selectedLog.severity] || severityConfig.info).color,
                      textTransform: 'capitalize',
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Details Grid */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                  <Typography variant="body2">{formatDate(selectedLog.createdAt)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">User</Typography>
                  <Typography variant="body2">
                    {selectedLog.userName || 'System'}
                    {selectedLog.userEmail && ` (${selectedLog.userEmail})`}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">IP Address</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selectedLog.ipAddress || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Request Path</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selectedLog.requestPath || 'N/A'}
                  </Typography>
                </Grid>
                {selectedLog.targetType && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Target Type</Typography>
                      <Typography variant="body2">{selectedLog.targetType}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Target Name</Typography>
                      <Typography variant="body2">{selectedLog.targetName || 'N/A'}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Description */}
              {selectedLog.description && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{selectedLog.description}</Typography>
                </Box>
              )}

              {/* For CREATE actions - Show full initial state */}
              {selectedLog.action === 'create' && selectedLog.metadata && (
                (() => {
                  const metadata = parseMetadata(selectedLog.metadata);
                  const initialState = metadata.initial_state;
                  if (!initialState) return null;
                  return (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        ✨ Created Configuration
                      </Typography>
                      <Paper variant="outlined" sx={{ overflow: 'hidden', bgcolor: '#f0fdf4', border: '1px solid #86efac' }}>
                        <Box sx={{ px: 2, py: 1, bgcolor: '#dcfce7', borderBottom: '1px solid #86efac' }}>
                          <Typography variant="caption" sx={{ color: '#166534', fontWeight: 600 }}>
                            + New {selectedLog.targetType}
                          </Typography>
                        </Box>
                        <Box sx={{ p: 2 }}>
                          <Typography 
                            component="pre" 
                            sx={{ 
                              fontFamily: 'monospace', 
                              fontSize: '0.75rem', 
                              color: '#166534',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              m: 0,
                            }}
                          >
                            {JSON.stringify(initialState, null, 2)}
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  );
                })()
              )}

              {/* For DELETE actions - Show deleted state */}
              {selectedLog.action === 'delete' && selectedLog.metadata && (
                (() => {
                  const metadata = parseMetadata(selectedLog.metadata);
                  const deletedState = metadata.deleted_state;
                  if (!deletedState) return null;
                  return (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        🗑️ Deleted Configuration
                      </Typography>
                      <Paper variant="outlined" sx={{ overflow: 'hidden', bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
                        <Box sx={{ px: 2, py: 1, bgcolor: '#fee2e2', borderBottom: '1px solid #fecaca' }}>
                          <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 600 }}>
                            − Removed {selectedLog.targetType}
                          </Typography>
                        </Box>
                        <Box sx={{ p: 2 }}>
                          <Typography 
                            component="pre" 
                            sx={{ 
                              fontFamily: 'monospace', 
                              fontSize: '0.75rem', 
                              color: '#dc2626',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              m: 0,
                            }}
                          >
                            {JSON.stringify(deletedState, null, 2)}
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  );
                })()
              )}

              {/* For UPDATE actions - Colored Diff View */}
              {selectedLog.action === 'update' && selectedLog.changes && selectedLog.changes !== '{}' && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    📝 Changes Made
                    <Chip label={`${Object.keys(parseChanges(selectedLog.changes)).length} fields`} size="small" />
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {Object.entries(parseChanges(selectedLog.changes)).map(([field, values]: [string, any]) => {
                      const formatValue = (val: any): string => {
                        if (val === null || val === undefined) return '(empty)';
                        if (typeof val === 'object') return JSON.stringify(val, null, 2);
                        return String(val);
                      };
                      const oldVal = formatValue(values.old);
                      const newVal = formatValue(values.new);
                      const isMultiline = oldVal.includes('\n') || newVal.includes('\n') || oldVal.length > 60 || newVal.length > 60;
                      
                      return (
                        <Paper key={field} variant="outlined" sx={{ overflow: 'hidden' }}>
                          {/* Field Header */}
                          <Box sx={{ 
                            px: 2, 
                            py: 1, 
                            bgcolor: '#f1f5f9', 
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                              {field}
                            </Typography>
                          </Box>
                          
                          {/* Diff Content */}
                          {isMultiline ? (
                            // Side by side for complex values
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                              <Box sx={{ 
                                p: 1.5, 
                                bgcolor: '#fef2f2', 
                                borderRight: '1px solid #e2e8f0',
                                minHeight: 40
                              }}>
                                <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                  − Before
                                </Typography>
                                <Typography 
                                  component="pre" 
                                  sx={{ 
                                    fontFamily: 'monospace', 
                                    fontSize: '0.75rem', 
                                    color: '#dc2626',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    m: 0,
                                  }}
                                >
                                  {oldVal}
                                </Typography>
                              </Box>
                              <Box sx={{ p: 1.5, bgcolor: '#f0fdf4', minHeight: 40 }}>
                                <Typography variant="caption" sx={{ color: '#166534', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                  + After
                                </Typography>
                                <Typography 
                                  component="pre" 
                                  sx={{ 
                                    fontFamily: 'monospace', 
                                    fontSize: '0.75rem', 
                                    color: '#16a34a',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    m: 0,
                                  }}
                                >
                                  {newVal}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            // Inline for simple values
                            <Box sx={{ p: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Box sx={{ 
                                  px: 1, 
                                  py: 0.5, 
                                  bgcolor: '#fef2f2', 
                                  borderRadius: 1,
                                  border: '1px solid #fecaca'
                                }}>
                                  <Typography sx={{ 
                                    fontFamily: 'monospace', 
                                    fontSize: '0.8rem', 
                                    color: '#dc2626',
                                    textDecoration: 'line-through',
                                  }}>
                                    {oldVal}
                                  </Typography>
                                </Box>
                                <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>→</Typography>
                                <Box sx={{ 
                                  px: 1, 
                                  py: 0.5, 
                                  bgcolor: '#f0fdf4', 
                                  borderRadius: 1,
                                  border: '1px solid #bbf7d0'
                                }}>
                                  <Typography sx={{ 
                                    fontFamily: 'monospace', 
                                    fontSize: '0.8rem', 
                                    color: '#16a34a',
                                    fontWeight: 500,
                                  }}>
                                    {newVal}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* User Agent */}
              {selectedLog.userAgent && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="caption" color="text.secondary">User Agent</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}>
                    {selectedLog.userAgent}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLog;
